import { Alert, ThemeProvider } from '@mui/material';
import {
  alpha,
  createTheme,
  darken,
  lighten,
} from '@mui/material/styles';
import { deDE as coreDeDE } from '@mui/material/locale';
import { deDE as dataGridDeDE } from '@mui/x-data-grid';
import { deDE } from '@mui/x-date-pickers/locales';
import { useEffect, useMemo } from 'react';
import './App.css';
import { ErrorBoundary } from './components/core/ErrorBoundary';
import { PageRouter } from './components/core/PageRouter';
import SecurityDriver from './components/core/SecurityDriver';
import { Loader } from './components/generic/Loader';
import { AlertProvider } from './context/AlertContext';
import { AuthProvider } from './context/AuthContext';
import { ConfirmationProvider } from './context/ConfirmationProvider';
import { useConfig } from './hooks/data';

function App() {
  const { config, isLoadingConfig } = useConfig();

  const theme = useMemo(() => {
    if (!config) return undefined;

    const primary = config.ci_color_primary;
    const secondary = config.ci_color_secondary;
    const textPrimary = config.ci_color_text;
    const pageBackground = lighten(primary, 0.95);
    const panelBackground = '#ffffff';
    const borderColor = alpha(primary, 0.16);
    const mutedText = alpha(textPrimary, 0.74);

    const fontSans =
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    const fontSerif =
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

    return createTheme(
      {
        palette: {
          primary: {
            main: primary,
            dark: darken(primary, 0.18),
            light: lighten(primary, 0.18),
          },
          secondary: {
            main: secondary,
            dark: darken(secondary, 0.14),
            light: lighten(secondary, 0.18),
          },
          text: {
            primary: textPrimary,
            secondary: mutedText,
          },
          background: {
            default: pageBackground,
            paper: panelBackground,
          },
          divider: borderColor,
        },
        shape: {
          borderRadius: 8,
        },
        typography: {
          fontFamily: fontSans,
          h1: { fontFamily: fontSerif, fontWeight: 700, letterSpacing: '-0.02em' },
          h2: { fontFamily: fontSerif, fontWeight: 700, letterSpacing: '-0.02em' },
          h3: { fontFamily: fontSerif, fontWeight: 700 },
          h4: { fontFamily: fontSerif, fontWeight: 700 },
          h5: { fontFamily: fontSerif, fontWeight: 700 },
          h6: { fontFamily: fontSans, fontWeight: 700, letterSpacing: '0.01em' },
          button: { fontFamily: fontSans, fontWeight: 700, letterSpacing: '0.01em', textTransform: 'none' },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              ':root': {
                colorScheme: 'light',
              },
              body: {
                background: `linear-gradient(180deg, ${lighten(primary, 0.94)} 0%, #f8f4ed 42%, ${pageBackground} 100%)`,
                color: textPrimary,
              },
              '#root': {
                minHeight: '100vh',
              },
              '::selection': {
                backgroundColor: alpha(primary, 0.18),
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: alpha('#ffffff', 0.88),
                backgroundImage: 'none',
                color: textPrimary,
                backdropFilter: 'blur(16px)',
                borderBottom: `1px solid ${borderColor}`,
                boxShadow: `0 10px 30px ${alpha(primary, 0.08)}`,
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                background: `linear-gradient(180deg, #ffffff 0%, ${lighten(primary, 0.97)} 100%)`,
                borderRight: `1px solid ${borderColor}`,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
              rounded: {
                borderRadius: 10,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                border: `1px solid ${borderColor}`,
                boxShadow: `0 18px 40px ${alpha(primary, 0.08)}`,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                border: `1px solid ${borderColor}`,
                boxShadow: `0 24px 50px ${alpha(primary, 0.18)}`,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                paddingInline: 16,
                minHeight: 42,
              },
              containedPrimary: {
                boxShadow: `0 12px 24px ${alpha(primary, 0.18)}`,
              },
              outlined: {
                borderWidth: 1.5,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              size: 'small',
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                backgroundColor: alpha('#ffffff', 0.92),
                borderRadius: 8,
                '& fieldset': {
                  borderColor: borderColor,
                },
                '&:hover fieldset': {
                  borderColor: alpha(primary, 0.4),
                },
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                border: `1px solid ${borderColor}`,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                minHeight: 46,
                borderRadius: 6,
                marginRight: 8,
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                height: '100%',
                borderRadius: 6,
                backgroundColor: alpha(primary, 0.1),
                zIndex: -1,
              },
              flexContainer: {
                gap: 4,
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: {
                color: mutedText,
                fontWeight: 700,
              },
            },
          },
          MuiBadge: {
            styleOverrides: {
              badge: {
                fontWeight: 700,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                fontWeight: 600,
              },
            },
          },
        },
      },
      deDE, // x-date-pickers translations
      dataGridDeDE, // x-data-grid translations
      coreDeDE // core translations
    );
  }, [config]);

  useEffect(() => {
    if (!config) return;

    const oldLink = document.getElementById('dynamic-favicon');
    if (oldLink && oldLink.parentNode) {
      oldLink.parentNode.removeChild(oldLink);
    }

    const link = document.createElement('link');
    link.id = 'dynamic-favicon';
    link.rel = 'shortcut icon';
    link.href = `data:image/x-icon;base64,${config.favicon}`;
    document.head.appendChild(link);

    document.title = `Mandantenportal - ${config.company_name}`;
  }, [config]);

  if (isLoadingConfig || !theme) {
    return <Loader />;
  }

  if (!config || (config && !config.App.find(x => x === 'dino_pro'))) {
    return (
      <Alert severity='warning'>
        Die Lizenz von DiNo konnte nicht überprüft werden!
      </Alert>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <AlertProvider>
          <SecurityDriver configObject={config}>
            <AuthProvider>
              <ConfirmationProvider>
                <PageRouter configObject={config} />
              </ConfirmationProvider>
            </AuthProvider>
          </SecurityDriver>
        </AlertProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
